import { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { type ColDef } from 'ag-grid-community';
import {
  type TireInstallation,
  type PerformanceLogDetail,
} from 'types/tire-installation.types';
import TireInstallationHandler from 'handlers/tire-installation.handler';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';

interface ViewInspectionsModalProps {
  open: boolean;
  onClose: () => void;
  installation: TireInstallation;
}

const DetailItem = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontSize={'14px'} fontWeight="medium">
      {value ?? '-'}
    </Typography>
  </Box>
);

export const ViewInspectionsModal = ({
  open,
  onClose,
  installation,
}: ViewInspectionsModalProps) => {
  const { t } = useTranslation();
  const [performanceLogs, setPerformanceLogs] = useState<
    PerformanceLogDetail[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const assets = useAppSelector((state) => state.assetReducer.assets);

  const tireInstallationHandler = new TireInstallationHandler();

  useEffect(() => {
    if (open) {
      void loadPerformanceLogs();
    }
  }, [open, installation.installationId]);

  const loadPerformanceLogs = async () => {
    setIsLoading(true);
    const logs = await tireInstallationHandler.getPerformanceLogs({
      installationId: installation.installationId,
      customerId,
    });
    setPerformanceLogs(logs);
    setIsLoading(false);
  };

  const handleDownloadImage = async (performanceLogId: string) => {
    await tireInstallationHandler.downloadPerformanceLogImage({
      performanceLogId,
      customerId,
    });
  };

  const asset = assets.find((a) => a.assetId === installation.assetId);
  const assetName = asset ? asset.bumperNumber : 'Unknown Asset';

  // Tire Condition renderer
  const TireConditionRenderer = (props: any) => {
    const condition = props.value;
    const getConditionColor = (condition: string) => {
      switch (condition) {
        case 'GOOD':
          return 'success';
        case 'WATCH':
          return 'warning';
        case 'REPAIRABLE':
          return 'warning';
        case 'SCRAP':
          return 'error';
        default:
          return 'default';
      }
    };

    return (
      <Chip
        label={condition}
        color={getConditionColor(condition)}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // Date formatter
  const DateRenderer = (props: any) => {
    if (!props.value) return <span>--</span>;
    return (
      <span>{convertStdDateString(props.value, 'MMM DD, YYYY HH:mm')}</span>
    );
  };

  // Boolean renderer
  const BooleanRenderer = (props: any) => {
    return (
      <Chip
        label={props.value ? 'Yes' : 'No'}
        color={props.value ? 'error' : 'success'}
        size="small"
        variant="outlined"
      />
    );
  };

  // File renderer with download
  const FileRenderer = (props: any) => {
    const s3Uri = props.value;
    const performanceLogId = props.data.id;

    if (!s3Uri) return <span style={{ opacity: 0.5 }}>--</span>;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <IconButton
          size="small"
          onClick={() => {
            void handleDownloadImage(performanceLogId);
          }}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const columnDefs: Array<ColDef<PerformanceLogDetail>> = [
    {
      field: 'timestamp',
      headerName: t('inspection_date'),
      minWidth: 180,
      cellRenderer: DateRenderer,
    },
    {
      field: 'currentTireCondition',
      headerName: t('condition'),
      minWidth: 130,
      cellRenderer: TireConditionRenderer,
    },
    {
      field: 'measuredAirPressure',
      headerName: t('air_pressure_psi'),
      minWidth: 150,
    },
    {
      field: 'averageTreadDepth',
      headerName: t('tread_depth_mm'),
      minWidth: 150,
    },
    {
      field: 'totalDistanceLog',
      headerName: t('total_distance_2'),
      minWidth: 140,
    },
    {
      field: 'totalEngineHoursLog',
      headerName: t('engine_hours'),
      minWidth: 140,
    },
    {
      field: 'damageReported',
      headerName: t('damage_reported'),
      minWidth: 160,
      cellRenderer: BooleanRenderer,
    },
    {
      field: 'sideWallDamage',
      headerName: t('sidewall_damage'),
      minWidth: 160,
      cellRenderer: BooleanRenderer,
    },
    {
      field: 'unevenTreadWear',
      headerName: t('uneven_tread_wear'),
      minWidth: 170,
      cellRenderer: BooleanRenderer,
    },
    {
      field: 'inspectionNotes',
      headerName: t('notes'),
      minWidth: 200,
      cellRenderer: (props: any) => {
        return props.value || <span style={{ opacity: 0.5 }}>--</span>;
      },
    },
    {
      field: 's3Uri',
      headerName: t('attachment'),
      minWidth: 140,
      cellRenderer: FileRenderer,
    },
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: 1200,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          outline: 'none',
          color: 'text.primary',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" component="h2">
            {t('tire_inspection_history')}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Body */}
        <Box
          sx={{ p: 3, maxHeight: '80vh', overflowY: 'auto' }}
          display={'flex'}
          flexDirection={'column'}
          gap={2}
        >
          {/* Installation Details Section */}
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 3, columnGap: 3 }}
          >
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('tire_serial_number')}
                value={installation.tireSerialNumber}
              />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem label={t('asset')} value={assetName} />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem label={t('position')} value={installation.position} />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('installation_date_2')}
                value={convertStdDateString(
                  installation.installationDate,
                  'MMM DD, YYYY'
                )}
              />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('odometer_at_install')}
                value={installation.odometerAtInstall || '-'}
              />
            </Box>

            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 12px)',
                  md: 'calc(25% - 18px)',
                },
              }}
            >
              <DetailItem
                label={t('engine_hours_at_install')}
                value={installation.engineHoursAtInstall || '-'}
              />
            </Box>

            {installation.latestPerformanceLog && (
              <>
                <Box
                  sx={{
                    width: {
                      xs: '100%',
                      sm: 'calc(50% - 12px)',
                      md: 'calc(25% - 18px)',
                    },
                  }}
                >
                  <DetailItem
                    label={t('current_condition')}
                    value={
                      <Chip
                        label={
                          installation.latestPerformanceLog.currentTireCondition
                        }
                        color={
                          installation.latestPerformanceLog
                            .currentTireCondition === 'GOOD'
                            ? 'success'
                            : installation.latestPerformanceLog
                                .currentTireCondition === 'WATCH' ||
                              installation.latestPerformanceLog
                                .currentTireCondition === 'REPAIRABLE'
                            ? 'warning'
                            : 'error'
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    }
                  />
                </Box>

                <Box
                  sx={{
                    width: {
                      xs: '100%',
                      sm: 'calc(50% - 12px)',
                      md: 'calc(25% - 18px)',
                    },
                  }}
                >
                  <DetailItem
                    label={t('last_inspection')}
                    value={convertStdDateString(
                      installation.latestPerformanceLog.timestamp,
                      'MMM DD, YYYY'
                    )}
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Inspection History Section */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('inspection_history')} ({performanceLogs.length})
            </Typography>

            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '300px',
                }}
              >
                <CircularProgress />
              </Box>
            ) : performanceLogs.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '300px',
                  backgroundColor: 'action.hover',
                  borderRadius: '8px',
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {t('no_inspection_history_found')}
                </Typography>
              </Box>
            ) : (
              <BaseTableList
                rowData={performanceLogs}
                columnDefs={columnDefs}
                pagination={true}
                paginationPageSize={10}
                tableStyles={{
                  height: `50vh`,
                  borderRadius: '8px',
                }}
                getRowId={(params: any) => params.data.id}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
